/**
 * API service – base URL from environment and shared fetch wrapper.
 * All backend calls should go through this (or auth service which uses it).
 */

const getBaseUrl = (): string => {
  const url = process.env.REACT_APP_API_BASE_URL;
  if (url === undefined || url === '') return '';
  return url.replace(/[,/]+$/, '').trim();
};

/** Base URL for DXR AI (templates, etc.) – use devaibackend, not main backend. */
const getDxrAiBaseUrl = (): string => {
  const url = process.env.REACT_APP_DXR_AI_BASE_URL;
  if (url === undefined || url === '') return 'https://devbackend.natoe.ai/api';
  return url.replace(/[,/]+$/, '').trim();
};

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  /** Field-level errors, e.g. { email: ["has already been taken"] } */
  details?: Record<string, string[]>;
}

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('auth_token') : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const apiKeyHeader = process.env.REACT_APP_API_KEY_HEADER;
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKeyHeader && apiKey) headers[apiKeyHeader] = apiKey;
  return headers;
}

/** Bearer (+ optional API key) only — no Content-Type so `fetch` can set multipart boundary. */
function getAuthHeadersForMultipart(): Record<string, string> {
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('auth_token') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const apiKeyHeader = process.env.REACT_APP_API_KEY_HEADER;
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKeyHeader && apiKey) headers[apiKeyHeader] = apiKey;
  return headers;
}

function parseJsonErrorResponse(text: string): {
  message: string;
  details?: Record<string, string[]>;
  code?: string;
} {
  let data: unknown = null;
  try {
    if (text) data = JSON.parse(text);
  } catch {
    return { message: text || 'Request failed' };
  }
  const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  const message =
    (obj && typeof obj.error === 'string' && obj.error)
    || (obj && typeof obj.message === 'string' && obj.message)
    || 'Request failed';
  const details =
    obj && typeof obj.details === 'object' && obj.details !== null && !Array.isArray(obj.details)
      ? (obj.details as Record<string, string[]>)
      : undefined;
  const code = obj && typeof obj.code === 'string' ? obj.code : undefined;
  return { message, details, code };
}

/**
 * Request to API. path should start with / (e.g. /auth/login).
 * Merges env base URL + path. Sends JSON body when body is provided.
 */
export async function request<T = unknown>(
  path: string,
  config: RequestConfig = {}
): Promise<T> {
  const base = getBaseUrl();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const method = config.method ?? 'GET';
  const headers = { ...getAuthHeaders(), ...config.headers };
  const body = config.body !== undefined ? JSON.stringify(config.body) : undefined;

  const res = await fetch(url, {
    method,
    headers,
    body,
    signal: config.signal,
    // Auth rides an HTTP-only cookie (_natoe_dxr_token) the backend sets at
    // login; it's unreadable from JS, so every request must send credentials.
    credentials: 'include',
  });

  const text = await res.text();
  let data: unknown = null;
  try {
    if (text) data = JSON.parse(text);
  } catch {
    // ignore
  }

  if (!res.ok) {
    const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const message =
      (obj && typeof obj.error === 'string' && obj.error)
      || (obj && typeof obj.message === 'string' && obj.message)
      || res.statusText
      || 'Request failed';
    const details =
      obj && typeof obj.details === 'object' && obj.details !== null && !Array.isArray(obj.details)
        ? (obj.details as Record<string, string[]>)
        : undefined;
    const err: ApiError = {
      message,
      status: res.status,
      code: obj && typeof obj.code === 'string' ? obj.code : undefined,
      details,
    };
    throw err;
  }

  return (data ?? text) as T;
}

/** Account controller paths (POST /login, POST /register, POST /change_password, POST /logout, POST /api/dxr/login/google) */
export const ACCOUNT_PATHS = {
  LOGIN: '/login',
  REGISTER: '/register',
  CHANGE_PASSWORD: '/change_password',
  LOGOUT: '/logout',
  RESET_PASSWORD: '/reset_password',
  SUBMIT_RESET_PASSWORD: '/submit_reset_password',
} as const;

/** GET /terms_and_conditions – returns full HTML document. */
export const TERMS_PATH = '/terms_and_conditions' as const;

/** Subscription controller – GET /subscription-plans (SubscriptionController :list_plans). Requires Bearer token. */
export const SUBSCRIPTION_PATHS = {
  LIST_PLANS: '/subscription-plans',
} as const;

/** Report controller – GET /get_reports, POST /add_report, POST /send_feedback, GET /get_global_dxr_templates. Requires Bearer token. */
export const REPORT_PATHS = {
  LIST: '/get_reports',
  ADD_REPORT: '/add_report',
  SEND_FEEDBACK: '/send_feedback',
  /** POST JSON { report_id, final_report } – persist the radiologist's final report (on copy/download). */
  SAVE_FINAL_REPORT: '/save_final_report',
  /** POST JSON { message } – emails a RIS/PACS/EHR integration request to the team. */
  REQUEST_INTEGRATION: '/request_integration',
  /** POST JSON { message } – emails a support query to support (nitesh@natoe.ai). */
  REQUEST_SUPPORT: '/request_support',
  GLOBAL_TEMPLATES: '/get_global_dxr_templates',
  /** POST multipart – field name `file` (zip). Same Bearer auth as other DXR routes. */
  UPLOAD_TEMPLATE: '/upload_template',
  /** POST JSON { Template_name, Content } to create a user-defined template. */
  ADD_TEMPLATE: '/add_template',
} as const;

/** GET /payment_history – payment / invoice rows for Transaction History. Requires Bearer token. Resolves to `{base}/payment_history` (base is typically `.../api/dxr`). */
export const PAYMENT_PATHS = {
  PAYMENT_HISTORY: '/payment_history',
} as const;

/** Text template endpoints – GET /get_text_templates, POST /save_text_template, POST /delete_text_template. */
export const TEXT_TEMPLATE_PATHS = {
  GET: '/get_text_templates',
  SAVE: '/save_text_template',
  DELETE: '/delete_text_template',
} as const;

/** Single item from GET /get_text_templates. */
export interface TextTemplate {
  template_name: string;
  template_content: string;
  category: string | null;
  source: 'global' | 'user';
}

/** Response from GET /get_text_templates – `{ data: TextTemplate[] }`. */
export interface GetTextTemplatesResponse {
  data: TextTemplate[];
}

/** Payload for POST /save_text_template – create, edit, or clone. Backend upserts by name (case-insensitive). */
export interface SaveTextTemplatePayload {
  template_name: string;
  template_content: string;
  category?: string;
}

/** Payload for POST /delete_text_template. is_global true → hide for user only; false → permanently delete user template. */
export interface DeleteTextTemplatePayload {
  template_name: string;
  is_global: boolean;
}

/** Payload for POST /add_report – input_report = dictated text, output_report = full generated report. */
export interface AddReportPayload {
  input_report: string;
  output_report: string;
  /** Which saved template produced the report (template mode only; omit in AI mode). */
  template_name?: string;
  /** 'ai' = free-form dictation, 'template' = a template was applied. */
  generation_mode?: 'ai' | 'template';
  /** Wall-clock generation time in whole seconds (dictation → generated). */
  time_taken?: number;
}

/** Response from POST /add_report – data.id is used for send_feedback. */
export interface AddReportResponse {
  data?: { id: string; inserted_at: string };
  message?: string;
  remaining_cases?: number;
}

/** Payload for POST /send_feedback – report_id from add_report response data.id. */
export interface SendFeedbackPayload {
  report_id: string;
  feedback: 'positive' | 'negative';
}

/**
 * Payload for POST /save_final_report – report_id from add_report response data.id;
 * final_report = the radiologist's final (possibly edited) report text, captured when they
 * copy or download it. output_report stays the raw DXR generation; this records what was used.
 */
export interface SaveFinalReportPayload {
  report_id: string;
  final_report: string;
}

/** Template item from GET /get_global_dxr_templates – show Template_name in dropdown, Content in right panel. */
export interface GlobalDxrTemplate {
  Content: string;
  Template_name: string;
}

/** Response from GET /get_global_dxr_templates – array or { data: GlobalDxrTemplate[] }. */
export type GetGlobalDxrTemplatesResponse =
  | GlobalDxrTemplate[]
  | { data: GlobalDxrTemplate[] };

/** Report item from GET /get_reports (ReportController :list). */
export interface ReportListItem {
  id: string;
  input_report: string;
  output_report: string;
  time_taken: number | null;
  dxr_user_id: string;
  inserted_at: string;
  updated_at: string;
}

/** Response from GET /get_reports – array or { data: ReportListItem[] }. */
export type GetReportsResponse = ReportListItem[] | { data: ReportListItem[] };

/** Plan item from GET /subscription-plans – exact backend response shape. */
export interface SubscriptionPlan {
  code: string;
  id: string;
  name: string;
  price: number;
  cases_limit: number;
  device_limit: number;
  duration_days: number;
  history_days: number;
  template_limit: number;
}

/** Response: { data: SubscriptionPlan[] } */
export interface SubscriptionPlansResponse {
  data: SubscriptionPlan[];
}

/**
 * Single row from GET /payment_history.
 * `amount` is in the smallest currency unit (e.g. paise for INR, cents for USD), same as Stripe.
 */
export interface PaymentHistoryItem {
  id: string;
  status: string;
  metadata: Record<string, unknown> | null;
  source: string;
  currency: string;
  amount: number;
  inserted_at: string;
  dxr_user_id: string;
  payment_id: string;
  subscription_id: string;
  subscription_plan_id: string;
  /** Display name for the plan (e.g. "Pro Monthly") when provided by API */
  subscription_plan_name?: string | null;
  /** Invoice / receipt URL when available */
  invoice_url?: string | null;
}

/** GET /payment_history – `{ data: PaymentHistoryItem[] }` or a raw array. */
export type PaymentHistoryResponse = PaymentHistoryItem[] | { data: PaymentHistoryItem[] };

/** Payload for POST /login (AccountController :login) */
export interface LoginPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/** Payload for POST /api/dxr/login/google – frontend sends Google id_token and optional profile. */
export interface GoogleLoginPayload {
  id_token: string;
  email?: string;
  name?: string;
}

/** Payload for POST /register (AccountController :register) */
export interface RegisterPayload {
  email: string;
  first_name: string;
  last_name?: string;
  password: string;
}

/** Payload for POST /verify_email – complete registration with OTP */
export interface VerifyEmailPayload {
  email: string;
  otp: string;
  first_name: string;
  last_name?: string;
  password: string;
}

/** Payload for POST /change_password (AccountController :change_password). Requires Bearer token. */
export interface ChangePasswordPayload {
  old_password: string;
  new_password: string;
}

/** Login API response – backend returns { data: { token, email, subscription, user_name, user_id } } */
export interface LoginSubscription {
  status: string;
  start_date: string;
  end_date: string;
  cases_limit: number;
  cases_used: number;
  remaining_cases: number;
}

export interface LoginApiData {
  token: string;
  email: string;
  user_name: string;
  user_id: string;
  subscription: LoginSubscription;
}

export interface LoginApiResponse {
  data: LoginApiData;
}

/** Legacy / register may return token + user shape */
export interface AccountAuthResponse {
  token: string;
  user: { id: string; email: string; username?: string; displayName?: string; [key: string]: unknown };
  expiresIn?: number;
}

// ── DXR AI backend (Python) — leaf DXR writes now live here, not the Elixir backend ──────────
/**
 * Host of the DXR AI backend (dxr_ai_backend / dxrprodaibackend) — the SAME FastAPI service the
 * admin dashboard + outreach hit; resolve it the same way they do. Do NOT use
 * REACT_APP_DXR_AI_BASE_URL: that var is the main-app templates base (e.g. prodappbackend/api), so
 * using it here mis-routes the /api/v1/dxr/* writes (wrong host + a doubled /api).
 */
const dxrAiHost = (): string =>
  (
    process.env.REACT_APP_OUTREACH_API ||
    (process.env.NODE_ENV === 'production' ? 'https://dxrprodaibackend.natoe.ai' : 'http://localhost:8084')
  ).replace(/\/+$/, '');

/** Current DXR user id (from the stored auth_user) — used to attribute writes server-side. */
const currentDxrUserId = (): string => {
  try {
    const raw = typeof window !== 'undefined' ? sessionStorage.getItem('auth_user') : null;
    const u = raw ? (JSON.parse(raw) as { id?: string; user_id?: string }) : null;
    return String(u?.id ?? u?.user_id ?? '');
  } catch {
    return '';
  }
};

/** POST to the DXR AI backend (api-key gated, like report generation). */
async function dxrAiPost<T = unknown>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${dxrAiHost()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'api-key': process.env.REACT_APP_REPORT_API_KEY || 'test123',
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    if (text) data = JSON.parse(text);
  } catch {
    // ignore non-JSON
  }
  if (!res.ok) {
    const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const message =
      (obj && typeof obj.detail === 'string' && obj.detail) ||
      (obj && typeof obj.error === 'string' && obj.error) ||
      res.statusText ||
      'Request failed';
    const err: ApiError = { message, status: res.status };
    throw err;
  }
  return (data ?? text) as T;
}

export const api = {
  getBaseUrl,
  get: <T = unknown>(path: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(path, { ...config, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(path, { ...config, method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(path, { ...config, method: 'PUT', body }),
  patch: <T = unknown>(path: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(path, { ...config, method: 'PATCH', body }),
  delete: <T = unknown>(path: string, config?: Omit<RequestConfig, 'method' | 'body'>) =>
    request<T>(path, { ...config, method: 'DELETE' }),

  /** Account controller endpoints */
  account: {
    /** POST /login – AccountController :login. Returns { data: { token, email, subscription, user_name, user_id } }. */
    login: (payload: LoginPayload) => request<LoginApiResponse>(ACCOUNT_PATHS.LOGIN, { method: 'POST', body: payload }),
    /** POST /api/dxr/login/google – Google login (expects same login payload shape). */
    googleLogin: (payload: GoogleLoginPayload) =>
      request<LoginApiResponse>('login/google', { method: 'POST', body: payload }),
    /** POST /register – AccountController :register. Sends OTP to email; use verifyEmail to complete. */
    register: (payload: RegisterPayload) =>
      request<LoginApiResponse | { success?: boolean }>(ACCOUNT_PATHS.REGISTER, { method: 'POST', body: payload }),
    /** POST /verify_email – verify OTP and complete registration. Returns same shape as /login. */
    verifyEmail: (payload: VerifyEmailPayload) =>
      request<LoginApiResponse>('/verify_email', { method: 'POST', body: payload }),
    /** POST /reset_password – request a password-reset email. Public. Always succeeds (anti-enumeration). */
    requestPasswordReset: (email: string) =>
      request<{ data: { message: string } }>(ACCOUNT_PATHS.RESET_PASSWORD, {
        method: 'POST',
        body: { email },
      }),
    /** POST /submit_reset_password – complete a reset using the emailed token. Public. */
    submitPasswordReset: (token: string, new_password: string) =>
      request<{ data: { message: string } }>(ACCOUNT_PATHS.SUBMIT_RESET_PASSWORD, {
        method: 'POST',
        body: { token, new_password },
      }),
    /** POST /change_password – AccountController :change_password. Requires Bearer token. */
    changePassword: (payload: ChangePasswordPayload) =>
      request<unknown>(ACCOUNT_PATHS.CHANGE_PASSWORD, { method: 'POST', body: payload }),
    /** POST /logout – AccountController :logout. Requires Bearer token. */
    logout: () => request<unknown>(ACCOUNT_PATHS.LOGOUT, { method: 'POST' }),
  },

  /** GET /api/dxr/terms_and_conditions – fetch terms and conditions as HTML. Returns raw HTML string. */
  getTermsHtml: async (): Promise<string> => {
    const base = getBaseUrl();
    const url = `${base}${TERMS_PATH.startsWith('/') ? '' : '/'}${TERMS_PATH}`;
    const res = await fetch(url, { method: 'GET', credentials: 'include' });
    if (!res.ok) throw new Error(res.statusText || 'Failed to load terms');
    return res.text();
  },

  /** Subscription controller – requires Bearer token */
  subscription: {
    /** GET /subscription-plans – SubscriptionController :list_plans */
    listPlans: () => request<SubscriptionPlansResponse>(SUBSCRIPTION_PATHS.LIST_PLANS, { method: 'GET' }),
  },

  /** Payment / billing – requires Bearer token */
  payment: {
    /**
     * GET /payment_history – transaction history for the account (see PaymentHistoryItem).
     * Sends `Authorization: Bearer <token>` using `sessionStorage` key `auth_token` (same as other authenticated routes).
     */
    getPaymentHistory: () => {
      const token = typeof window !== 'undefined' ? sessionStorage.getItem('auth_token') : null;
      return request<PaymentHistoryResponse>(PAYMENT_PATHS.PAYMENT_HISTORY, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    },
  },

  /** Report controller – requires Bearer token */
  report: {
    /** GET /get_reports – ReportController :list */
    listReports: () => request<GetReportsResponse>(REPORT_PATHS.LIST, { method: 'GET' }),
    /** POST /add_report – ReportController. input_report = dictated text, output_report = full report from API. */
    addReport: (payload: AddReportPayload) =>
      request<AddReportResponse>(REPORT_PATHS.ADD_REPORT, { method: 'POST', body: payload }),
    /** POST /send_feedback – report_id from add_report response data.id, feedback: 'positive' | 'negative'. */
    sendFeedback: (payload: SendFeedbackPayload) =>
      request<unknown>(REPORT_PATHS.SEND_FEEDBACK, { method: 'POST', body: payload }),
    /** POST /api/v1/dxr/save_final_report on the DXR AI backend — UPDATE dxr_reports.final_report. */
    saveFinalReport: (payload: SaveFinalReportPayload) =>
      dxrAiPost('/api/v1/dxr/save_final_report', payload),
    /**
     * RIS/PACS/EHR integration request. The MAIN (Elixir) backend SENDS the email — required; the
     * DXR AI backend additionally PERSISTS it to dxr_user_requests. The email is primary (its result
     * drives the UX); the DB-persist is best-effort so a persistence hiccup never blocks a request
     * that was already emailed.
     */
    requestIntegration: async (message: string) => {
      const res = await request<{ data: { message: string } }>(
        REPORT_PATHS.REQUEST_INTEGRATION, { method: 'POST', body: { message } },
      );
      try {
        await dxrAiPost('/api/v1/dxr/request_integration', { message, dxr_user_id: currentDxrUserId() });
      } catch (e) {
        console.warn('[dxr] integration request emailed but DB-persist failed', e);
      }
      return res;
    },
    /**
     * Support query. The MAIN (Elixir) backend SENDS the email — required; the DXR AI backend
     * additionally PERSISTS it to dxr_user_requests. Email is primary; DB-persist is best-effort.
     */
    requestSupport: async (message: string) => {
      const res = await request<{ data: { message: string } }>(
        REPORT_PATHS.REQUEST_SUPPORT, { method: 'POST', body: { message } },
      );
      try {
        await dxrAiPost('/api/v1/dxr/request_support', { message, dxr_user_id: currentDxrUserId() });
      } catch (e) {
        console.warn('[dxr] support request emailed but DB-persist failed', e);
      }
      return res;
    },
    /** POST /add_template – create a new user-defined template { Template_name, Content }. */
    addTemplate: (payload: { Template_name: string; Content: string }) =>
      request<unknown>(REPORT_PATHS.ADD_TEMPLATE, { method: 'POST', body: payload }),
    /** GET get_global_dxr_templates – list of templates (uses DXR AI base URL, not main backend). */
    getGlobalDxrTemplates: () => {
      const base = getDxrAiBaseUrl();
      const path = `${base}${REPORT_PATHS.GLOBAL_TEMPLATES.startsWith('/') ? '' : '/'}${REPORT_PATHS.GLOBAL_TEMPLATES}`;
      return request<GetGlobalDxrTemplatesResponse>(path, { method: 'GET' });
    },
    /**
     * POST multipart to `{REACT_APP_API_BASE_URL}/upload_template` – same as curl `--form 'file=@...'`.
     * Uses session `auth_token` Bearer header; does not set Content-Type (boundary set by browser).
     */
    uploadTemplate: async <T = unknown>(file: File): Promise<T> => {
      const base = getBaseUrl();
      const path = REPORT_PATHS.UPLOAD_TEMPLATE;
      const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? path : `/${path}`}`;
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeadersForMultipart(),
        body: form,
        credentials: 'include',
      });
      const text = await res.text();
      let data: unknown = null;
      try {
        if (text) data = JSON.parse(text);
      } catch {
        // ignore
      }
      if (!res.ok) {
        const parsed = parseJsonErrorResponse(text);
        const err: ApiError = {
          message: parsed.message,
          status: res.status,
          code: parsed.code,
          details: parsed.details,
        };
        throw err;
      }
      return (data ?? text) as T;
    },
  },

  /** Text template endpoints – requires Bearer token */
  templates: {
    /** GET /get_text_templates – merged global + user templates. */
    getTextTemplates: () => request<GetTextTemplatesResponse>(TEXT_TEMPLATE_PATHS.GET, { method: 'GET' }),
    /** POST /save_text_template – create, edit, or clone (backend upserts by name). */
    saveTextTemplate: (payload: SaveTextTemplatePayload) =>
      request<unknown>(TEXT_TEMPLATE_PATHS.SAVE, { method: 'POST', body: payload }),
    /** POST /delete_text_template – hide global or permanently delete user template. */
    deleteTextTemplate: (payload: DeleteTextTemplatePayload) =>
      request<unknown>(TEXT_TEMPLATE_PATHS.DELETE, { method: 'POST', body: payload }),
  },
};

export default api;
